import {DELIVERY_MONTHS_MAP, isFuturesTicker} from '../../utils';

interface HedgePositionPick {
    name: string;
    ticker: string;  // Format: @SPXW SPX Call 6500 2024-12-25
    qty: number;
}

interface ParsedPosition {
    name: string;
    symbol: string;      // SPX(W)
    optionType: string;  // Call or Put
    strike: number;
    expiration: Date;
    qty: number;
}

function formatHedge(positions: HedgePositionPick[]): string {
    if (positions.length === 0) {
        throw new Error('Positions array cannot be empty');
    }

    // Parse all positions
    const parsed = positions.map(p => parsePosition(p));

    // Validate all same option type
    const firstType = parsed[0].optionType;
    if (!parsed.every(p => p.optionType === firstType)) {
        throw new Error('All positions must be same option type (all Calls or all Puts)');
    }

    // Group by expiration date
    const groupedByExpiration = groupByExpiration(parsed);

    // Sort groups by expiration date (earliest first)
    const sortedGroups = Array.from(groupedByExpiration.entries())
        .sort((a, b) => a[0].getTime() - b[0].getTime());

    // Generate text block for each expiration group
    const blocks: string[] = [];
    const hedgeName = positions[0].name;
    const optionTypePlural = firstType === 'Call' ? 'Calls' : 'Puts';
    const hasMultipleExpirations = sortedGroups.length > 1;

    sortedGroups.forEach(([expiration, groupPositions], index) => {
        // Only add Back/Front label if there are multiple expirations
        const label = hasMultipleExpirations
            ? (index === 0 ? 'Back' : 'Front')
            : null;

        // Calculate DTE for this expiration
        const dte = calculateDTE(expiration);

        // Sort positions by strike descending
        groupPositions.sort((a, b) => b.strike - a.strike);

        // Build header
        const typeLabel = label ? `${optionTypePlural}, ${label}` : optionTypePlural;
        const header = `-- ${hedgeName} (${typeLabel}) [DTE=${dte}]--`;

        // Build position lines
        const lines = groupPositions.map(p => formatPositionLine(p));

        // Combine header and lines
        blocks.push([header, ...lines].join('\n'));
    });

    // Join all blocks with blank line separator
    return blocks.join('\n\n');
}

function groupByExpiration(positions: ParsedPosition[]): Map<Date, ParsedPosition[]> {
    const groups = new Map<string, ParsedPosition[]>();

    positions.forEach(pos => {
        // Use date string as key for grouping
        const dateKey = pos.expiration.toISOString().split('T')[0];

        if (!groups.has(dateKey)) {
            groups.set(dateKey, []);
        }
        groups.get(dateKey)!.push(pos);
    });

    // Convert back to Map with Date keys
    const result = new Map<Date, ParsedPosition[]>();
    groups.forEach((positions, dateKey) => {
        const date = new Date(dateKey + 'T00:00:00');
        result.set(date, positions);
    });

    return result;
}

function makeFriendlyFuturesSymbol(ticker: string): string {
    const monthCode = ticker.substring(ticker.length - 2, ticker.length - 1);
    const yearDigit = ticker.substring(ticker.length - 1);
    const root = ticker.substring(0, ticker.length - 2);
    const monthName = DELIVERY_MONTHS_MAP[monthCode];
    return `${root} ${monthName}2${yearDigit}`;
}

function parsePosition(position: HedgePositionPick): ParsedPosition {
    // Ticker format: @SPXW SPX Call 6500 2024-12-25
    const tickerMatch = position.ticker.match(/@(\w+)\s+(\w+)\s+(Call|Put)\s+(\d+(?:\.\d+)?)\s+(\d{4}-\d{2}-\d{2})/);

    if (!tickerMatch) {
        throw new Error(`Invalid ticker format: ${position.ticker}`);
    }

    const [, optSymbol, root, optionType, strikeStr, dateStr] = tickerMatch;

    // Convert SPXW → SPX(W), futures tickers to friendly name (ESH6 → ES Mar26), otherwise use root as-is
    let symbol: string;
    if (optSymbol === 'SPXW') {
        symbol = `${root}(W)`;
    } else if (isFuturesTicker(root)) {
        symbol = makeFriendlyFuturesSymbol(root);
    } else {
        symbol = root;
    }

    const strike = parseFloat(strikeStr);
    // Parse as UTC by adding 'Z' suffix
    const expiration = new Date(dateStr + 'T00:00:00Z');

    return {
        name: position.name,
        symbol,
        optionType,
        strike,
        expiration,
        qty: position.qty
    };
}
function calculateDTE(expiration: Date): number {
    // Get current date in ET timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });

    const parts = formatter.formatToParts(new Date());
    const year = parseInt(parts.find(p => p.type === 'year')!.value);
    const month = parseInt(parts.find(p => p.type === 'month')!.value) - 1;
    const day = parseInt(parts.find(p => p.type === 'day')!.value);

    // Create today's date at midnight UTC
    const todayET = Date.UTC(year, month, day);

    // Create expiration date at midnight UTC
    const expirationDay = Date.UTC(
        expiration.getFullYear(),
        expiration.getMonth(),
        expiration.getDate()
    );

    // Calculate difference in days
    const diffMs = expirationDay - todayET;
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

    return diffDays;
}

function formatPositionLine(position: ParsedPosition): string {
    // Format quantity in 4-char column, centered
    const qtyStr = (position.qty > 0 ? '+' : '') + position.qty.toString();
    const qtyFormatted = centerAlign(qtyStr, 4);

    // Format date as "Mon, 05-Dec-25"
    const dayOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][position.expiration.getDay()];
    const day = position.expiration.getDate().toString().padStart(2, '0');
    const month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][position.expiration.getMonth()];
    const year = position.expiration.getFullYear().toString().slice(-2);
    const dateFormatted = `${dayOfWeek}, ${day}-${month}-${year}`;

    return `${qtyFormatted} | ${position.symbol} ${position.optionType} ${position.strike} ${dateFormatted}`;
}

function centerAlign(str: string, width: number): string {
    if (str.length >= width) {
        return str;
    }

    const totalPadding = width - str.length;
    const leftPadding = Math.floor(totalPadding / 2);
    const rightPadding = totalPadding - leftPadding;

    return ' '.repeat(leftPadding) + str + ' '.repeat(rightPadding);
}

// Export
export { formatHedge, HedgePositionPick };